import { useCallback, useRef, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';

export type ScanStatus = 'success' | 'error' | 'duplicate';

/**
 * useScanModalFlow
 * A reusable hook that centralizes ScanModal UX state and rules.
 * Responsibilities:
 * - Manage modal visibility and status (success | error | duplicate)
 * - Queue duplicate notifications when the same item is scanned while its modal is already open
 */
export function useScanModalFlow<T = unknown>() {
  const [scanStatus, setScanStatus] = useState<ScanStatus>('success');
  const [scannedItem, setScannedItem] = useState<T | null>(null);

  const scanModal = useDisclosure();
  const [modalKey, setModalKey] = useState(0);

  const forceOpen = useCallback(() => {
    setModalKey((k) => k + 1);
    scanModal.onOpen();
  }, [scanModal]);

  const lastModalItemUidRef = useRef<string | null>(null);

  const handleModalClose = useCallback(() => {
    scanModal.onClose();
  }, [scanModal]);

  const openSuccess = useCallback(
    (item: T | null, uid: string | null) => {
      setScannedItem(item ?? null);
      setScanStatus('success');
      lastModalItemUidRef.current = uid ?? null;
      forceOpen();
    },
    [forceOpen],
  );

  const openError = useCallback(() => {
    setScanStatus('error');
    lastModalItemUidRef.current = null;
    forceOpen();
  }, [forceOpen]);

  const handleDuplicate = useCallback(
    (uid: string, item: T | null) => {
      const lastUid = lastModalItemUidRef.current;
      if (!scanModal.isOpen || lastUid !== uid) {
        setScanStatus('duplicate');
        setScannedItem(item);
        lastModalItemUidRef.current = uid;
        forceOpen();
      }
    },
    [scanModal.isOpen, forceOpen],
  );

  return {
    isOpen: scanModal.isOpen,
    onOpen: scanModal.onOpen,
    onClose: handleModalClose,
    modalKey,
    forceOpen,
    scanStatus,
    setScanStatus,
    scannedItem,
    setScannedItem,
    openSuccess,
    openError,
    handleDuplicate,
  } as const;
}
